import {
  buildBarSeries,
  buildDataShadowSeries,
  buildLineSeries,
  type EChartConfig,
  type EChartSeriesConfig,
} from "echart-table-library";
import {
  PRINT_OVERVIEW_CHART_COLORS,
} from "@/shared/constants/chartColors";
import type { ChartSort, ChartSortOption } from "@/shared/types";
import type { TranslateFn } from "@/shared/i18n";
import type { WidgetToolbarConfig } from "@/components/Widget/WidgetToolbar";
import type { PrintOverviewPoint, OverviewGranularity } from "./types";
import type { OverviewChartRow } from "./logic";

/**
 * Single style source for BOTH print-overview charts (paper-usage parity):
 * current vs. last-year side-by-side bars + secondary-axis comparison
 * lines. The COLOR VALUES live in the central registry
 * (`@/shared/constants/chartColors`) and are re-exported under their stable
 * names, so the two charts can never drift apart visually.
 *
 * Hover has NO highlight (`emphasis.disabled` via the builders) — the
 * page-level selection is the only distinct color, baked into the series
 * data as per-item `itemStyle` on EVERY column of the selected period
 * (cross-granularity: a month selection can light up many day columns —
 * the library builders accept a LIST of highlight indices).
 */

export const OVERVIEW_BAR_WIDTH = "30%";

/** Shared grid insets (room for the dual rotated axis names). */
export const OVERVIEW_GRID = { top: 56, left: 88, right: 72 } as const;

/** dataZoom window per granularity (day/week/month pan; year fits). */
export const OVERVIEW_ZOOM_WINDOW: Record<OverviewGranularity, number> = {
  day: 30,
  week: 26,
  month: 12,
  year: 24,
};

/**
 * Shared dataZoom config: a fixed per-granularity window with the wheel
 * panning (never zooming). NOTE: the slider's data shadow CANNOT be
 * redirected by option — ECharts always traces the FIRST series on the
 * axis (`_prepareDataShadowInfo` ignores `seriesIndex`, verified in
 * echarts@6.1.0 source) — which is why the invisible shadow series exists
 * (see `buildShadowSeries`).
 */
export function buildOverviewDataZoom(granularity: OverviewGranularity): {
  window: number;
  side: "left";
  inside: { zoomOnMouseWheel: false };
} {
  return {
    window: OVERVIEW_ZOOM_WINDOW[granularity],
    side: "left",
    inside: { zoomOnMouseWheel: false },
  };
}

/**
 * Invisible line series (MUST be series index 0) that feeds the slider's
 * data shadow: ECharts traces the first axis series for the miniature line
 * inside the scrollbar, and the requirement is that it reflects "Printed
 * Length" (the CURRENT bar) — while the first BAR series must stay
 * "Printed Length Last Year" (last-year column on the left). Built by the
 * LIBRARY helper (`buildDataShadowSeries` — the framework workaround lives
 * there); a line series participates in neither bar layout nor the column
 * order, so prepending it costs nothing visually: transparent line, no
 * symbols, no hover, no legend entry (legend.data is explicit) and no
 * tooltip row (the `printOverviewTooltip` callback filters this name out).
 */
export const OVERVIEW_SHADOW_SERIES_NAME = "__printOverviewShadow";

export function buildShadowSeries(): EChartSeriesConfig {
  return buildDataShadowSeries({
    name: OVERVIEW_SHADOW_SERIES_NAME,
    field: "printedLength",
  });
}

/**
 * Axis trigger + the shadow-filtering formatter callback. The dark tooltip
 * chrome (background/text) is the LIBRARY default now
 * (`tooltipChromeDefaults`) — no per-widget styling needed.
 */
export const OVERVIEW_TOOLTIP = {
  trigger: "axis",
  formatter: { $callback: "printOverviewTooltip" },
} as const;

/** Toolbar capability declaration (section 23) — identical for both charts. */
export const OVERVIEW_TOOLBAR_CONFIG: WidgetToolbarConfig = {
  exportCsv: true,
  exportExcel: true,
  exportImage: true,
};

/**
 * The SECONDARY comparison metric of one chart (the comparison lines on
 * the right axis): dataset fields, i18n keys and sort-label keys. The
 * primary metric (Printed Length bars, left axis) is identical for both
 * charts, so only this side is parameterized.
 */
export interface OverviewSecondaryMetric {
  /** Current-period field of the chart POINTS (drives the axis max). */
  field: keyof Pick<PrintOverviewPoint, "jobCount" | "inkUsage">;
  /** Current/last-year fields of the chart ROWS (line series fields). */
  lastYearField: keyof Pick<OverviewChartRow, "jobCountLastYear" | "inkUsageLastYear">;
  /** i18n keys: series names (legend + tooltip) and the secondary axis. */
  nameKey: string;
  lastYearNameKey: string;
  axisNameKey: string;
  /** i18n keys of the sort dropdown labels (may differ from the series keys). */
  sortLabelKey: string;
  sortLastYearLabelKey: string;
}

/** Chart 1's secondary metric: Job Count. */
export const JOB_COUNT_METRIC: OverviewSecondaryMetric = {
  field: "jobCount",
  lastYearField: "jobCountLastYear",
  nameKey: "charts.series.jobCount",
  lastYearNameKey: "charts.series.jobCountLastYear",
  axisNameKey: "charts.axis.jobCount",
  sortLabelKey: "charts.series.jobCount",
  sortLastYearLabelKey: "charts.series.jobCountLastYear",
};

/** Chart 2's secondary metric: Ink Usage (L). */
export const INK_USAGE_METRIC: OverviewSecondaryMetric = {
  field: "inkUsage",
  lastYearField: "inkUsageLastYear",
  nameKey: "charts.series.inkUsage",
  lastYearNameKey: "charts.series.inkUsageLastYear",
  axisNameKey: "charts.axis.inkUsageL",
  sortLabelKey: "charts.tooltip.inkUsage",
  sortLastYearLabelKey: "charts.series.inkUsageLastYear",
};

/**
 * Comparison bar series (current OR last-year column of one metric): same
 * builder defaults as the shell widgets — the highlight is a LIST of column
 * indices (library multi-index selection): the cross-chart selection can
 * mark any number of columns sharing the selected period.
 */
export function buildComparisonBarSeries(options: {
  name: string;
  field: string;
  color: string;
  highlightIndices: number[];
  yAxisIndex?: number;
}): EChartSeriesConfig {
  return buildBarSeries({
    name: options.name,
    field: options.field,
    color: options.color,
    barWidth: OVERVIEW_BAR_WIDTH,
    yAxisIndex: options.yAxisIndex,
    selectedIndex: options.highlightIndices,
    highlightColor: PRINT_OVERVIEW_CHART_COLORS.barHighlight,
    highlightGlow: PRINT_OVERVIEW_CHART_COLORS.highlightGlow,
  });
}

/** Comparison line series — hover shows nothing (selection-only highlight). */
export function buildComparisonLineSeries(options: {
  name: string;
  field: string;
  color: string;
  yAxisIndex?: number;
}): EChartSeriesConfig {
  return buildLineSeries({
    name: options.name,
    field: options.field,
    color: options.color,
    yAxisIndex: options.yAxisIndex,
  });
}

/**
 * The ONE comparison config both charts render (their previous two configs
 * differed only in the secondary metric): shadow series at index 0, the
 * last-year/current bar pair on the printed-length axis, the metric's
 * last-year/current line pair on the secondary axis, legend at the BOTTOM
 * (requirement), fixed y extents and the per-granularity dataZoom window.
 *
 * `t` translates the axis/series names from the i18n JSON; defaults to the
 * i18next instance's current language (tests).
 */
export function buildPrintedLengthComparisonConfig(options: {
  highlightIndices: number[];
  printedLengthMax: number;
  secondaryMax: number;
  metric: OverviewSecondaryMetric;
  granularity: OverviewGranularity;
  t: TranslateFn;
}): EChartConfig {
  const { highlightIndices, printedLengthMax, secondaryMax, metric, granularity, t } = options;

  return {
    legend: {
      bottom: 0,
      // Current series first (dark shades), last year second — one legend
      // item per series, in the visual pairing order.
      data: [
        t("charts.series.overviewPrintedLength"),
        t("charts.series.overviewPrintedLengthLy"),
        t(metric.nameKey),
        t(metric.lastYearNameKey),
      ],
    },
    grid: { ...OVERVIEW_GRID, bottom: 96 },
    yAxis: [
      {
        name: t("charts.axis.printedLengthFt"),
        nameLocation: "middle",
        nameGap: 56,
        // FIXED extent over the WHOLE dataset (paper-usage parity): without
        // it the axis rescales to the visible dataZoom window on every pan.
        // Peaks include the last-year bars — they share this axis.
        min: 0,
        max: printedLengthMax,
        axisLabel: {},
        splitLine: { show: true },
      },
      {
        name: t(metric.axisNameKey),
        nameLocation: "middle",
        nameGap: 48,
        min: 0,
        // Data-driven (nice peak) — the comparison line never clips.
        max: secondaryMax,
        axisLabel: {},
        // Secondary gridlines would compete with the primary axis's.
        splitLine: { show: false },
      },
    ],
    series: [
      // Index 0: the invisible shadow series — feeds the scrollbar's data
      // shadow with the CURRENT "Printed Length" values (see above).
      buildShadowSeries(),
      // Bars: LAST YEAR (light) first = left column of the pair, CURRENT
      // (dark) second — both on the printed-length axis, both wearing the
      // selection highlight on the selected period's columns.
      buildComparisonBarSeries({
        name: t("charts.series.overviewPrintedLengthLy"),
        field: "printedLengthLastYear",
        color: PRINT_OVERVIEW_CHART_COLORS.barLastYear,
        highlightIndices,
      }),
      buildComparisonBarSeries({
        name: t("charts.series.overviewPrintedLength"),
        field: "printedLength",
        color: PRINT_OVERVIEW_CHART_COLORS.barBase,
        highlightIndices,
      }),
      // Lines: the metric's comparison pair on the secondary axis.
      buildComparisonLineSeries({
        name: t(metric.lastYearNameKey),
        field: metric.lastYearField,
        color: PRINT_OVERVIEW_CHART_COLORS.lineLastYear,
        yAxisIndex: 1,
      }),
      buildComparisonLineSeries({
        name: t(metric.nameKey),
        field: metric.field,
        color: PRINT_OVERVIEW_CHART_COLORS.lineBase,
        yAxisIndex: 1,
      }),
    ],
    tooltip: OVERVIEW_TOOLTIP,
    // Horizontal scrollbar under the x axis (fixed window per drill level,
    // data shadow of the "Printed Length" series); wheel zoom is disabled —
    // scrolling never zooms (paper-usage parity).
    dataZoom: buildOverviewDataZoom(granularity),
  };
}

/** Sortable fields (chart-sort plan): period + the chart's four series
 *  (current + last-year) — client-side, applied before the dataset rows
 *  are built. Last-year fields sort by the paired period's value. */
export function buildOverviewSortOptions(
  metric: OverviewSecondaryMetric,
): ChartSortOption[] {
  return [
    { field: "period", labelKey: "charts.tooltip.date" },
    { field: "printedLength", labelKey: "charts.series.overviewPrintedLength" },
    { field: "printedLengthLastYear", labelKey: "charts.series.overviewPrintedLengthLy" },
    { field: metric.field, labelKey: metric.sortLabelKey },
    { field: metric.lastYearField, labelKey: metric.sortLastYearLabelKey },
  ];
}

/** Concrete default sort (chronological) — both charts' previous behavior. */
export const OVERVIEW_DEFAULT_SORT: ChartSort = { id: "period", desc: false };
